/**
 * =========================================================================================
 *     Copyright © 2025 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   HealthScoreCard.tsx
 *     Description     :   Summary card showing health statistics
 *     Author          :   Peter Dobler
 *     Created         :   2026-01-19
 *     Modified        :   2026-01-19 | Peter Dobler | Initial implementation
 *     Constitution    :   v1.11.0 (Specification Testing Standards Edition)
 *     Feature         :   028-lakehouse-maintenance
 * =========================================================================================
 */

import React from 'react';
import { CheckCircle2, AlertTriangle, AlertCircle } from 'lucide-react';
import { Card, Spinner } from '../ui';

interface HealthScoreCardProps {
  title: string;
  count: number;
  total: number;
  status: 'healthy' | 'degraded' | 'unhealthy';
  loading?: boolean;
}

const statusConfig = {
  healthy: {
    icon: CheckCircle2,
    color: 'var(--color-status-success)',
  },
  degraded: {
    icon: AlertTriangle,
    color: 'var(--color-status-warning)',
  },
  unhealthy: {
    icon: AlertCircle,
    color: 'var(--color-status-danger)',
  },
} as const;

export const HealthScoreCard: React.FC<HealthScoreCardProps> = ({
  title,
  count,
  total,
  status,
  loading = false,
}) => {
  const config = statusConfig[status];
  const Icon = config.icon;
  // Spec 298 FR-004 (evidence/rates.md #71): no percentage over nothing to count; this read 0%.
  const percentage = total > 0 ? Math.round((count / total) * 100) : null;

  return (
    <Card
      isHoverable
      className="h-full border-2 transition-transform duration-200 hover:-translate-y-1"
      style={{ borderColor: config.color }}
    >
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-base font-medium text-[var(--color-text-secondary)]">{title}</h3>
        <Icon className="h-8 w-8" style={{ color: config.color }} />
      </div>

      {loading ? (
        <div className="flex justify-center py-4">
          <Spinner size="lg" />
        </div>
      ) : (
        <>
          <div className="mb-2 flex items-baseline">
            <span className="text-4xl font-bold" style={{ color: config.color }}>
              {count}
            </span>
            <span className="ml-2 text-base text-[var(--color-text-secondary)]">
              / {total}
            </span>
          </div>

          {percentage != null ? (
            <>
              <div className="mb-2 h-2 w-full overflow-hidden rounded-full bg-[var(--color-bg-subtle)]">
                <div
                  className="h-full transition-[width] duration-300"
                  style={{ width: `${percentage}%`, backgroundColor: config.color }}
                />
              </div>

              <p className="text-right text-sm text-[var(--color-text-secondary)]">{percentage}%</p>
            </>
          ) : (
            <p className="text-right text-sm text-[var(--color-text-secondary)]">no lakehouses</p>
          )}
        </>
      )}
    </Card>
  );
};

export default HealthScoreCard;
